import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createDepartment,
  deleteDepartment,
  fetchDepartments,
  toggleDepartmentStatus,
  updateDepartment,
} from "../services/api";
import { setAlert } from "../store/slices/alertSlice";
import { Modal, Pagination } from "react-bootstrap";
import MasterListToolbar from "./MasterListToolbar";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

const PAGE_SIZE = 10;

const emptyForm = {
  departmentName: "",
  departmentCode: "",
  isActive: true,
};

const mapDepartmentToForm = (department) => ({
  departmentName: department.departmentName || "",
  departmentCode: department.departmentCode || "",
  isActive: department.isActive !== false,
});

const DepartmentManagement = () => {
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteDepartmentId, setDeleteDepartmentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDepartments, setTotalDepartments] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { searchInput, setSearchInput, appliedSearch, clearSearch } = useDebouncedSearch();

  const loadDepartments = useCallback(async (page, search) => {
    setLoading(true);
    try {
      const response = await fetchDepartments(page, PAGE_SIZE, search);
      setDepartments(response.data.departments || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalDepartments(response.data.totalDepartments || 0);
      setCurrentPage(Number(response.data.currentPage || page));
    } catch (error) {
      dispatch(setAlert({ type: "danger", message: "Error loading departments" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadDepartments(currentPage, appliedSearch);
  }, [currentPage, appliedSearch, loadDepartments]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch]);

  const handleClearSearch = () => {
    clearSearch();
    setCurrentPage(1);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setFormData(emptyForm);
    setEditingDepartmentId(null);
  };

  const openAddModal = () => {
    setFormData(emptyForm);
    setEditingDepartmentId(null);
    setShowFormModal(true);
  };

  const openEditModal = (department) => {
    setFormData(mapDepartmentToForm(department));
    setEditingDepartmentId(department._id);
    setShowFormModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.departmentName.trim() || !formData.departmentCode.trim()) {
      dispatch(setAlert({ type: "danger", message: "Department name and code are required" }));
      return;
    }

    const payload = {
      departmentName: formData.departmentName.trim(),
      departmentCode: formData.departmentCode.trim().toUpperCase(),
    };

    if (editingDepartmentId) {
      payload.isActive = formData.isActive;
    }

    setSaving(true);
    try {
      if (editingDepartmentId) {
        await updateDepartment(editingDepartmentId, payload);
        dispatch(setAlert({ type: "success", message: "Department updated successfully" }));
      } else {
        await createDepartment(payload);
        dispatch(setAlert({ type: "success", message: "Department created successfully" }));
      }

      closeFormModal();
      loadDepartments(currentPage, appliedSearch);
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Unable to save department",
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDepartment(deleteDepartmentId);
      dispatch(setAlert({ type: "success", message: "Department deleted successfully" }));
      setShowDeleteModal(false);
      loadDepartments(currentPage, appliedSearch);
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Unable to delete department",
      }));
    }
  };

  const handleToggleStatus = async (department) => {
    const nextStatus = department.isActive === false;
    try {
      await toggleDepartmentStatus(department._id, nextStatus);
      dispatch(setAlert({
        type: "success",
        message: nextStatus ? "Department enabled successfully" : "Department disabled successfully",
      }));
      loadDepartments(currentPage, appliedSearch);
    } catch (error) {
      dispatch(setAlert({
        type: "danger",
        message: error.response?.data?.error || "Error updating department status",
      }));
    }
  };

  const getStatusBadge = (department) => {
    if (department.isActive === false) {
      return <span className="badge bg-secondary">Disabled</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  const pageStart = totalDepartments === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, totalDepartments);

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h4 className="mb-0">Department Management</h4>
            <small className="text-muted">
              {totalDepartments} department{totalDepartments === 1 ? "" : "s"} total
              {appliedSearch ? ` matching "${appliedSearch}"` : ""}
            </small>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
            <MasterListToolbar
              searchValue={searchInput}
              onSearchChange={(event) => setSearchInput(event.target.value)}
              searchPlaceholder="Search name or code..."
              searchAriaLabel="Search departments"
              onClear={handleClearSearch}
              showClear={Boolean(appliedSearch)}
            />
            <button type="button" className="btn btn-primary btn-sm" onClick={openAddModal}>
              <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>
              Add Department
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading</span>
              </div>
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center text-muted py-5">
              {appliedSearch
                ? <>No departments found for <strong>{appliedSearch}</strong>.</>
                : <>No departments found. Click <strong>Add Department</strong> to create one.</>}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Department Name</th>
                    <th>Department Code</th>
                    <th>Status</th>
                    <th className="text-end" style={{ width: "180px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((department) => (
                    <tr key={department._id}>
                      <td className="fw-semibold">{department.departmentName}</td>
                      <td>
                        <span className="badge bg-light text-dark border">{department.departmentCode}</span>
                      </td>
                      <td>{getStatusBadge(department)}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-outline-primary btn-sm me-1"
                          title="Edit Department"
                          type="button"
                          onClick={() => openEditModal(department)}
                        >
                          <i className="bi bi-pencil" aria-hidden="true"></i>
                        </button>
                        <button
                          className={`btn btn-sm me-1 ${department.isActive === false ? "btn-outline-success" : "btn-outline-secondary"}`}
                          title={department.isActive === false ? "Enable Department" : "Disable Department"}
                          type="button"
                          onClick={() => handleToggleStatus(department)}
                        >
                          <i
                            className={`bi ${department.isActive === false ? "bi-check-circle" : "bi-slash-circle"}`}
                            aria-hidden="true"
                          ></i>
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          title="Delete Department"
                          type="button"
                          onClick={() => {
                            setDeleteDepartmentId(department._id);
                            setShowDeleteModal(true);
                          }}
                        >
                          <i className="bi bi-trash" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
              <small className="text-muted">
                Showing {pageStart}-{pageEnd} of {totalDepartments}
              </small>
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                />
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index}
                    active={index + 1 === currentPage}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editingDepartmentId ? "Edit Department" : "Add Department"}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">
                Department Name <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                value={formData.departmentName}
                onChange={(event) => setFormData((prev) => ({ ...prev, departmentName: event.target.value }))}
                placeholder="Emergency"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">
                Department Code <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                value={formData.departmentCode}
                onChange={(event) => setFormData((prev) => ({ ...prev, departmentCode: event.target.value }))}
                placeholder="ER"
              />
            </div>
            {editingDepartmentId && (
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="department-is-active"
                  checked={formData.isActive}
                  onChange={(event) => setFormData((prev) => ({ ...prev, isActive: event.target.checked }))}
                />
                <label className="form-check-label" htmlFor="department-is-active">
                  Department active (available in forms and reports)
                </label>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-outline-secondary" onClick={closeFormModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : editingDepartmentId ? "Update Department" : "Create Department"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will soft-delete the department using <strong>isDeleted</strong>. The record will be
          deactivated (<strong>isActive = false</strong>), hidden from this list, and
          <strong> deletedBy</strong> / <strong>deletedAt</strong> will be recorded.
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DepartmentManagement;
